import {Auth0Client} from '@auth0/auth0-spa-js';
import TextUtilsSvcMod from '@Services/TextUtils.svc.js';
/** @typedef {import('./Auth.svc')} Types */
//
export class Auth {
	#SCOPE = 'openid profile email offline_access';

	constructor(
		$log,
		$cookies,
		$q,
		ENV,
		API,
		ROLE_ALIASES,
		AUTH_KEYS,
		AUTH0_DOMAIN,
		AUTH0_CLIENT_ID,
		HOST_URL,
		TextUtilsSvc
	) {
		'ngInject';
		this.$cookies = $cookies;
		this.$log = $log;
		this.$q = $q;
		this.TextUtilsSvc = TextUtilsSvc;
		this.AUTH_KEYS = AUTH_KEYS;
		this.SUPER_ALIAS = ROLE_ALIASES.super;
		this.ADMIN_ALIAS = ROLE_ALIASES.admin;
		this.CONSULTANT_ALIAS = ROLE_ALIASES.consultant;
		this.user = this.getUser();
		this.auth0Config = {
			domain: AUTH0_DOMAIN,
			clientId: AUTH0_CLIENT_ID,
			scope: this.#SCOPE,
			name: 'OCA Client',
			redirectUri: `${HOST_URL}/user/auth`,
			useRefreshTokens: true,
		};
		this.authClient = new Auth0Client({
			...this.auth0Config,
			authorizationParams: {
				// 'docker' consuming API from docker localhort
				audience: ENV === 'docker' ? 'https://api-dev.oncallair.com' : API,
				redirect_uri: `${HOST_URL}/user/auth`,
			},
		});
	}

	isLoggedIn() {
		return this.getUser() ? Object.prototype.hasOwnProperty.call(this.user, 'id') : false;
	}

	currentUser() {
		return this.getUser();
	}

	/** @param {Types.User} userObj */
	isSuper(userObj) {
		return this.hasFeatureAlias(userObj, this.SUPER_ALIAS);
	}

	/** @param {Types.User} userObj */
	isAdmin(userObj) {
		return this.hasFeatureAlias(userObj, this.ADMIN_ALIAS);
	}

	/** @param {Types.User} userObj */
	isConsultant(userObj) {
		return this.hasFeatureAlias(userObj, this.CONSULTANT_ALIAS);
	}

	/** @param {Types.User} userObj */
	updateUser(userObj) {
		if (!userObj) this.$log.error('User object param is required');
		const decodedProviderName = this.TextUtilsSvc.decodeHtmlEntities(userObj.service_provider_name);
		const userData = Object.assign(this.user, userObj, {service_provider_name: decodedProviderName});
		this.$cookies.putObject(this.AUTH_KEYS.userKey, userData);
		return this.currentUser();
	}

	/**
	 * @param {Types.User} user
	 * @param {string} feature
	 */
	hasFeatureAlias(user, feature) {
		if (!user || !user.features || !feature) {
			this.$log.error('Invalid params: user, feature');
			return false;
		}
		return user.features?.includes(feature);
	}

	/** @param {Types.User} userObj */
	setUser(userObj) {
		if (!userObj) this.$log.error('User object param is required');

		let userData;
		if (userObj.hasOwnProperty(this.AUTH_KEYS.nameSpace)) {
			const {
				direct_pricing_mode,
				first_name,
				last_name,
				logo_url,
				mobile_phone,
				oca_user_id,
				order_url,
				roles,
				service_provider_id,
				service_provider_name,
				theme,
				using_custom_bundle,
				using_multi_system,
			} = userObj[this.AUTH_KEYS.nameSpace];
			userData = {
				id: oca_user_id,
				email: userObj.email,
				first_name,
				last_name,
				logo_url,
				mobile_phone,
				oca_user_id,
				order_url,
				service_provider_id,
				service_provider_name: this.TextUtilsSvc.decodeHtmlEntities(service_provider_name),
				theme,
				using_custom_bundle,
				using_multi_system,
				direct_pricing_mode,
				capabilities: [],
				features: roles,
			};
		} else {
			userObj.service_provider_name = this.TextUtilsSvc.decodeHtmlEntities(userObj.service_provider_name);
			userData = Object.assign({capabilities: []}, userObj);
		}

		this.$cookies.putObject(this.AUTH_KEYS.userKey, userData);
		return this.currentUser();
	}

	getUser() {
		this.user = this.$cookies.getObject(this.AUTH_KEYS.userKey);
		if (!this.user || !this.user?.id) this.user = null;
		return this.user;
	}

	/**
	 * Single source of truth for "is there a fully-usable authenticated user".
	 * A valid user must carry both an `id` and a `service_provider_id`; callers should use
	 * this instead of re-implementing the shape check.
	 * @returns {boolean}
	 */
	hasValidUser() {
		const user = this.getUser();
		return Boolean(user && user.id && user.service_provider_id);
	}

	getUserCapabilities() {
		this.user = this.getUser();
		return this.user.capabilities;
	}

	/** @param {string[]} capabilities */
	setUserCapabilities(capabilities) {
		this.user = this.getUser();
		this.user.capabilities = capabilities;
		this.updateUser(this.user);
		return this.user;
	}

	/** @param {string[]} features */
	setUserFeatures(features) {
		this.user = this.getUser();
		if (!this.user) {
			this.$log.error('current user must exist as a cookie');
			return false;
		}
		this.user.features = features;
		this.updateUser(this.user);
		return this.user;
	}

	/** @param {string} role */
	getAlias(role) {
		let alias;
		switch (role) {
			case 'admin':
				alias = this.ADMIN_ALIAS;
				break;
			case 'consultant':
				alias = this.CONSULTANT_ALIAS;
				break;
			default:
				this.$log.error('No user feature match');
				break;
		}
		return alias;
	}

	removeSession() {
		this.$cookies.remove(this.AUTH_KEYS.userKey);
		this.$cookies.remove(this.AUTH_KEYS.tokenKey);
	}

	getAuth0Client() {
		return this.authClient;
	}

	/** @param {string} token */
	setAccessToken(token) {
		if (typeof token !== 'string') {
			this.$log.error('Token must be a string');
			return false;
		}

		this.$cookies.put(this.AUTH_KEYS.tokenKey, token);
	}

	getAccessToken() {
		return this.$cookies.get(this.AUTH_KEYS.tokenKey);
	}
}
//
let compMod = angular.module('mod.svc.AuthSvc', [TextUtilsSvcMod]).service('AuthSvc', Auth);
export default (compMod = compMod.name);
